import ChangeStatusDialog from '@/components/common/change-status-dialog/change-status-dialog';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';
import { composeUri } from '@/utils/compose';
import {
    AVATAR_GROUP_QUOTE_TYPE,
    AVATARS,
    AVATARS_ADD_GROUP_AVATARS,
    DISORDER_CHANGE_STATUS_NODE_LIST,
    GROUP_AVATARS,
    INCIDENT_SERVICE_DEVIS,
    PLACES,
    TRACEABILITY_TRACKING_V2_CHANGE_NODE_1,
} from '@/utils/CONST';
import useGetAlphaId from '@/utils/useGetAlphaId';
import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { Fragment, useState } from 'react';
import { Button, useCreate, useListContext, useRecordSelection, useUnselectAll } from 'react-admin';
import { useParams } from 'react-router';

interface Props {
    setMultiSelectionState: any;
    setCustomBackdropLoadingState: any;
    setAction: (action: 'create_quote' | 'status' | undefined) => void;
    action: 'create_quote' | 'status' | undefined;
}

const addZeros = (value: number) => String(value).padStart(4, '0');

const DisordersTreatmentListBulkActionsButtons = ({
    setMultiSelectionState,
    setCustomBackdropLoadingState,
    setAction,
    action,
}: Props) => {
    const [changeStatusDialogState, setChangeStatusDialogState] = useState<boolean>(false);

    const handleChangeStatusDialogOpen = () => {
        setChangeStatusDialogState(true);
    };

    const handleChangeStatusDialogClose = () => {
        setChangeStatusDialogState(false);
    };

    const { isLoadingGetAlphaId, getAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_DEVIS,
    });
    const [selectedAvatarsIds] = useRecordSelection({
        resource: AVATARS,
        storeKey: 'disordersTreatmentList',
    });
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const { currentUser } = useGetCurrentUser();
    const queryClient = useQueryClient();
    const unselectAll = useUnselectAll(AVATARS, 'disordersTreatmentList');
    const { placeId, siteId } = useParams();

    const { avatar, isLoading: isLoadingPlace } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, placeId),
        queryKey: `${PLACES}_${siteId}`,
    });
    const placeAlphaId = avatar?.alphaId;

    const { refetch } = useListContext();

    const stopLoading = () => setCustomBackdropLoadingState(false);

    const getChangeNodePromises = (avatarsUriIds: string[]) =>
        avatarsUriIds.map((avatarUriId: string) => {
            return create(
                TRACEABILITY_TRACKING_V2_CHANGE_NODE_1,
                {
                    data: {
                        avatar: avatarUriId,
                        idForNodeHumanReadable: '0011',
                        version: 1,
                        extraData: [],
                    },
                },
                {
                    returnPromise: true,
                },
            );
        });

    const handleAvatarGroupCreation = () => {
        setCustomBackdropLoadingState(true);

        const yearLastTwoDigits = moment().year().toString().slice(-2);

        getAlphaId()
            .then((data: { counter: number; identifier: string }) => {
                create(
                    GROUP_AVATARS,
                    {
                        data: {
                            name: `${yearLastTwoDigits}${addZeros(data.counter)}`,
                            company: currentUser?.company,
                            type: AVATAR_GROUP_QUOTE_TYPE,
                            identifiers: placeAlphaId != null ? [placeAlphaId] : [],
                        },
                    },
                    {
                        onSuccess: (avatarGroup: any) => {
                            create(
                                AVATARS_ADD_GROUP_AVATARS,
                                {
                                    data: {
                                        groupAvatar: avatarGroup.id,
                                        avatars: selectedAvatarsIds,
                                    },
                                },
                                {
                                    onSuccess: async () => {
                                        try {
                                            queryClient.invalidateQueries({ queryKey: [AVATARS, 'getList'] });
                                            unselectAll();
                                            const promises = getChangeNodePromises(selectedAvatarsIds);
                                            await Promise.all(promises);
                                            refetch();
                                            setMultiSelectionState(false);
                                            setAction(undefined);
                                        } finally {
                                            stopLoading();
                                        }
                                    },
                                    onError: stopLoading,
                                },
                            );
                        },
                        onError: stopLoading,
                    },
                );
            })
            .catch(stopLoading);
    };

    const handleAction = () => {
        if (action === 'create_quote') {
            handleAvatarGroupCreation();
        } else if (action === 'status') {
            handleChangeStatusDialogOpen();
        }
    };

    return (
        <Fragment>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                }}
            >
                <Button
                    label={'ra.action.confirm'}
                    onClick={handleAction}
                    disabled={isLoadingGetAlphaId || isLoadingCreate || isLoadingPlace || !placeAlphaId}
                />
            </Box>

            {changeStatusDialogState && (
                <ChangeStatusDialog
                    open={changeStatusDialogState}
                    onClose={handleChangeStatusDialogClose}
                    choices={Object.values(DISORDER_CHANGE_STATUS_NODE_LIST)}
                    useAvatarsChangeNodeApi
                    storeKey="disordersTreatmentList"
                    onLoadingStart={() => setCustomBackdropLoadingState(true)}
                    onLoadingEnd={stopLoading}
                    onSuccess={() => {
                        setMultiSelectionState(false);
                        setAction(undefined);
                    }}
                />
            )}
        </Fragment>
    );
};

export default DisordersTreatmentListBulkActionsButtons;
